import type {
  CompTrialResults,
  HeatNoString,
  IndividualResult,
  TrialMap
} from '@/components/results/store-comp-results'
import { cleanUpScoreValueForDisplay } from '@/components/results/results-service'
import type {
  FieldResults_AthleteResult,
  SocketFieldResultsResult,
  SocketResultMessagePayloadFieldResults
} from '@/components/results/socket/socket-field-results/socket-field-results-models'
import * as LiveFeedService from '@/components/results/socket/live-feed/live-feed-service'
import { simpleClone } from '@/services/common-service-utils'
import type { EventGroupId, EventIdString } from '@/services/common'

export function mapSocketFieldResultsResultToIndividualResult(
  socketFieldResultsResult: SocketFieldResultsResult,
  animateThisAthleteId?: number,
  fieldResultAthlete?: FieldResults_AthleteResult
): IndividualResult {
  return {
    laneNo: socketFieldResultsResult.laneNo,
    position: socketFieldResultsResult.position,
    wind: socketFieldResultsResult.wind,
    athlete: socketFieldResultsResult.athlete,
    athleteId: socketFieldResultsResult.athleteId,
    affilation: socketFieldResultsResult.clubName,
    bibNo: socketFieldResultsResult.bibNo.toString(),
    score: cleanUpScoreValueForDisplay(socketFieldResultsResult.scoreValue),
    qualify: socketFieldResultsResult.qualify,
    animateThisAthlete: socketFieldResultsResult.athleteId === animateThisAthleteId,
    fieldResultAthlete:
      socketFieldResultsResult.athleteId === animateThisAthleteId ? fieldResultAthlete : undefined,
    ageGroup: socketFieldResultsResult.ageGroup
  }
}

export function mapSocketFieldResultsResultsToIndividualResults(
  socketResultMessagePayloadFieldResults: SocketResultMessagePayloadFieldResults,
  animateThisAthleteId?: number,
  fieldResultAthlete?: FieldResults_AthleteResult
): Record<HeatNoString, IndividualResult[]> {
  const result: Record<HeatNoString, IndividualResult[]> = {}
  for (const heat of socketResultMessagePayloadFieldResults.ranking.heats) {
    result[heat.heatNo.toString()] = heat.results.map((socketFieldResultsResult) => {
      return mapSocketFieldResultsResultToIndividualResult(
        socketFieldResultsResult,
        animateThisAthleteId,
        fieldResultAthlete
      )
    })
  }
  return result
}

export function howManyHeats(
  socketResultMessagePayloadFieldResults: SocketResultMessagePayloadFieldResults
): number {
  return socketResultMessagePayloadFieldResults.ranking.heats.length
}

export function getTrialResultsForAthlete(
  socketResultMessagePayloadFieldResults: SocketResultMessagePayloadFieldResults,
  athleteId: number | string
): Partial<TrialMap> | undefined {
  if (!socketResultMessagePayloadFieldResults.allTrials) {
    return undefined
  }
  /*
  convert
  [
    {
        "trial": "t1",
        "result": 29.63
    },
    {
        "trial": "t2",
        "result": 25
    },
    {
        "trial": "t3",
        "result": 17.84
    }
]

to
{
  t1: 29.63,
  t2: 25,
  t3: 17.84
}
   */
  return socketResultMessagePayloadFieldResults.allTrials.reduce((acc, trial) => {
    acc[trial.trial] = trial.result
    return acc
  }, {} as Partial<TrialMap>)
}

export function updateTrialResults(
  payload: SocketResultMessagePayloadFieldResults,
  compTrialResults: CompTrialResults
): CompTrialResults {
  // if (!payload.ranking.trials) {
  //   return compTrialResults
  // }

  const eventIdString: EventIdString = payload.ranking.eventGroup.eventNo.toString()
  const eventGroupId: EventGroupId = payload.ranking.eventGroup.id

  // How  many athlete results have been sent up, if card loses connection, it will send up
  // multiple athlete results
  const athletes = LiveFeedService.getFieldResultsAthletes(payload)
  const athleteIds = Object.keys(athletes)

  if (athleteIds.length !== 1) {
    return compTrialResults
  }

  const athleteIdString = Number(athleteIds[0]).toString()
  const compTrialResultsUpdated = simpleClone(compTrialResults)

  // const eventTrialMap = compTrialResultsUpdated[eventIdString]
  // if (!eventTrialMap) {
  //   compTrialResultsUpdated[eventIdString] = {}
  // }
  const eventTrialMap = compTrialResultsUpdated[eventGroupId]
  if (!eventTrialMap) {
    compTrialResultsUpdated[eventGroupId] = {}
  }

  const athleteTrialMap = getTrialResultsForAthlete(payload, athleteIdString)
  if (athleteTrialMap) {
    // compTrialResultsUpdated[eventIdString][athleteIdString] = athleteTrialMap
    compTrialResultsUpdated[eventGroupId][athleteIdString] = athleteTrialMap
  }
  return compTrialResultsUpdated
}

export function updateTrialResultsTest(
  payload: SocketResultMessagePayloadFieldResults,
  compTrialResults: CompTrialResults
): any {
  if (!payload.ranking.trials) {
    return compTrialResults
  }

  const eventIdString: EventIdString = payload.ranking.eventGroup.eventNo.toString()

  const athletes = LiveFeedService.getFieldResultsAthletes(payload)
  const athleteIds = Object.keys(athletes)

  if (athleteIds.length !== 1) {
    return compTrialResults
  }

  const athleteIdString = Number(athleteIds[0]).toString()
  const compTrialResultsUpdated = simpleClone(compTrialResults)

  const eventTrialMap = compTrialResultsUpdated[eventIdString]
  if (!eventTrialMap) {
    compTrialResultsUpdated[eventIdString] = {}
  }

  //  Trying to get the trial results for the athlete from payload...but could be undefined...!!!!
  let athleteTrialMap = getTrialResultsForAthlete(payload, athleteIdString)
  if (athleteTrialMap) {
    eventTrialMap[athleteIdString] = athleteTrialMap
  } else {
    athleteTrialMap = compTrialResultsUpdated[eventIdString][athleteIdString]
    if (!athleteTrialMap) {
      compTrialResultsUpdated[eventIdString][athleteIdString] = {}
    }
  }
  compTrialResultsUpdated[eventIdString][athleteIdString]['t1'] = '10.00'

  return {
    eventIdString
  }
}
